import { Check, RefreshCw } from "lucide-react";
import type { DetailType } from "#shared/schemas/entityDetail.ts";
import type { MbLinkEntityType } from "#shared/schemas/mbEnrich.ts";
import { cn } from "@web/lib/utils.ts";
import {
  useMbLink,
  useMbStatus,
  useStartMbBatch,
  useStartMbEntity,
} from "@web/features/settings/mbApi.ts";

function asLinkType(type: DetailType): MbLinkEntityType | null {
  if (type === "artist" || type === "track" || type === "album") return type;
  return null;
}

/**
 * Compact circular sync affordance on the entity overview — fetch / refetch
 * MusicBrainz (+ AB for tracks). Outline + refresh = never fetched; filled +
 * check = already have data. Available to every signed-in user.
 */
export function SheetSyncButton({
  entityType,
  entityId,
}: {
  entityType: DetailType;
  entityId: string;
}) {
  const linkType = asLinkType(entityType);
  const isPlaylist = entityType === "playlist";
  const isLocalPlaylist = entityType === "local_playlist";
  const show = linkType !== null || isPlaylist || isLocalPlaylist;

  const linkQ = useMbLink(linkType, show && linkType ? entityId : null);
  const statusQ = useMbStatus(null, {
    enabled: show,
    refetchInterval: 3_000,
  });
  const startEntity = useStartMbEntity();
  const startBatch = useStartMbBatch();

  if (!show) return null;

  const run = statusQ.data?.run;
  const batchBusy = run?.status === "running" || run?.status === "paused";
  const thisEntityRun =
    run?.mode === "entity" &&
    linkType &&
    run.entity_type === linkType &&
    run.entity_id === entityId;
  const thisPlaylistRun =
    run?.mode === "batch" &&
    ((isPlaylist && run.playlist_id === entityId) ||
      (isLocalPlaylist && run.local_playlist_id === entityId));
  const mineBusy = Boolean((thisEntityRun || thisPlaylistRun) && batchBusy);

  const fetched =
    linkType != null
      ? Boolean(linkQ.data?.entity && linkQ.data.link?.mbid)
      : false;

  const pending = startEntity.isPending || startBatch.isPending || mineBusy;
  const blocked = batchBusy && !mineBusy;

  function onClick() {
    if (pending || blocked) return;
    if (isPlaylist || isLocalPlaylist) {
      startBatch.mutate({
        size: "all",
        force: false,
        ...(isPlaylist ? { playlist_id: entityId } : {}),
        ...(isLocalPlaylist ? { local_playlist_id: entityId } : {}),
        quiet: true,
      });
      return;
    }
    if (!linkType) return;
    startEntity.mutate({
      entity_type: linkType,
      entity_id: entityId,
      force: false,
      quiet: true,
    });
  }

  const label = pending
    ? "Syncing…"
    : blocked
      ? "Queued behind batch"
      : fetched
        ? "Check data"
        : "Fetch data";

  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={blocked && !pending}
      onClick={onClick}
      className={cn(
        "inline-flex size-7 shrink-0 items-center justify-center rounded-full border transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60",
        "disabled:opacity-50",
        fetched
          ? "border-accent bg-accent text-accent-contrast hover:bg-accent-hover"
          : "border-line bg-panel text-muted hover:border-muted hover:text-text",
        pending && "border-accent text-accent",
      )}
    >
      {pending ? (
        <RefreshCw className="size-3.5 animate-spin" aria-hidden />
      ) : fetched ? (
        <Check className="size-3.5" strokeWidth={2.5} aria-hidden />
      ) : (
        <RefreshCw className="size-3.5" aria-hidden />
      )}
    </button>
  );
}
